import { isNil } from 'ramda'
import { ActionFunctionAny } from 'redux-actions'

import { ReportFilter } from '@driftt/api-types'

import { ReportPage } from 'types/pages'
import { combineReducers, createReducer } from 'utils/redux'

import * as actions from './actions'
import { IFilterState } from './types'
import { calculateLastSevenDaysTimePeriod } from './utils'

const DEFAULT_TIME_PERIOD = calculateLastSevenDaysTimePeriod()
const UPDATE_REPORT_TIME_PERIOD_KEYS = [
  'startDate',
  'endDate',
  'prevStart',
  'prevEnd',
]

const updateReportTimePeriodHandler = ({ draft, action: { payload } }) => {
  UPDATE_REPORT_TIME_PERIOD_KEYS.forEach((key) => {
    draft[key] = payload[key]
  })
}

const convertFilter = (
  filter: ReportFilter,
  teams: any[],
  reportType: ReportPage
): ReportFilter => {
  const agentIdPropertyName = [
    ReportPage.Conversation,
    ReportPage.Team,
  ].includes(reportType)
    ? 'firstAgentId'
    : 'agentId'
  if (filter.property === 'workspace') {
    const workspacesSelected = filter.values || []
    const workspaceAgents = new Set<number>()

    teams
      .filter((team) => workspacesSelected.includes(team.workspaceId))
      .forEach((team) =>
        team.members.forEach(workspaceAgents.add, workspaceAgents)
      )

    return {
      operation: filter.operation,
      property: agentIdPropertyName,
      values: Array.from(workspaceAgents),
    }
  }

  if (filter.property === 'team') {
    const teamAgents = new Set<number>()
    filter.values?.map((team: any) =>
      team.members.forEach(teamAgents.add, teamAgents)
    )
    return {
      operation: filter.operation,
      property: agentIdPropertyName,
      values: Array.from(teamAgents),
    }
  }

  if (filter.property === 'teammate') {
    return {
      operation: filter.operation,
      property: agentIdPropertyName,
      values: filter.values,
    }
  }

  if (filter.property === 'inbox') {
    return {
      operation: filter.operation,
      property: 'inboxId',
      values: filter.values,
    }
  }

  if (filter.property === 'classification') {
    return {
      operation: filter.operation,
      property: 'classificationId',
      values: filter.values,
    }
  }

  if (filter.property === 'widgetStatus') {
    return {
      operation: filter.operation,
      property: 'offline',
      values: filter.values,
    }
  }

  if (filter.property === 'botStatus') {
    return {
      operation: filter.operation,
      property: 'botStatus',
      values: filter.values,
    }
  }

  if (filter.property === 'classification') {
    return {
      operation: filter.operation,
      property: 'classificationId',
      values: filter.values,
    }
  }

  return filter
}

const timePeriod = createReducer(
  {
    startDate: DEFAULT_TIME_PERIOD!.startDate,
    endDate: DEFAULT_TIME_PERIOD!.endDate,
    prevStart: DEFAULT_TIME_PERIOD!.prevStart,
    prevEnd: DEFAULT_TIME_PERIOD!.prevEnd,
  },
  {
    [actions.updateReportTimePeriod.toString()]: updateReportTimePeriodHandler,
  }
)

const updateWorkspaceAgents = ({ action: { payload } }) => {
  if (isNil(payload)) {
    return null
  }

  const { teams, workspaceSelected } = payload
  const workspaceAgents = new Set()

  teams
    .filter((team) => team.workspaceId === workspaceSelected.value)
    .map((team) => team.members.forEach(workspaceAgents.add, workspaceAgents))

  return Array.from(workspaceAgents)
}

const workspaceAgents = createReducer(null, {
  [actions.updateWorkspaceAgents.toString()]: updateWorkspaceAgents,
})

const updateCompoundQuery = ({ action: { payload } }) => {
  if (isNil(payload) || isNil(payload.newCompoundFilter)) {
    return null
  }

  const { newCompoundFilter, teams, reportType } = payload

  return newCompoundFilter.filters.map((filter) =>
    convertFilter(filter, teams, reportType)
  )
}

const updateCompoundFilter = ({ action: { payload } }) => {
  if (isNil(payload)) {
    return null
  }
  return payload.newCompoundFilter
}

const compoundFilter = createReducer(null, {
  [actions.updateCompoundFilter.toString()]: updateCompoundFilter,
})
const compoundQuery = createReducer(null, {
  [actions.updateCompoundFilter.toString()]: updateCompoundQuery,
})

const createFilterObjectReducer = <T>(actionType: ActionFunctionAny<T>) =>
  createReducer(null, {
    [actionType.toString()]: ({ action }) =>
      isNil(action.payload) ? null : action.payload,
  })

const createConversationFilterReducer = <T>(actionType: ActionFunctionAny<T>) =>
  createReducer(null, {
    [actions.clearConversationFilters.toString()]: () => null,
    [actionType.toString()]: ({ action }) =>
      isNil(action.payload) ? null : action.payload,
  })

const botStatus = createConversationFilterReducer(actions.updateReportBotStatus)
const widgetStatus = createConversationFilterReducer(
  actions.updateReportWidgetStatus
)
const agentsSelected = createConversationFilterReducer(
  actions.updateReportAgentsSelected
)
const inboxSelected = createConversationFilterReducer(
  actions.updateInboxSelected
)
const teamSelected = createFilterObjectReducer(actions.updateTeamSelected)
const workspaceSelected = createFilterObjectReducer(
  actions.updateWorkspaceSelected
)
const selectedClassificationId = createConversationFilterReducer(
  actions.updateClassificationSelected
)
const automationPlaybookSelected = createFilterObjectReducer(
  actions.updateAutomationPlaybookSelected
)

export default combineReducers<IFilterState>({
  timePeriod,
  botStatus,
  widgetStatus,
  agentsSelected,
  inboxSelected,
  teamSelected,
  workspaceSelected,
  workspaceAgents,
  selectedClassificationId,
  automationPlaybookSelected,
  compoundFilter,
  compoundQuery,
})
